import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faStar, faClock, faPercent } from '@fortawesome/free-solid-svg-icons';

import { RootState } from '@share/utils';
import { Responsive } from '@share/utils';
import { Routes } from '@share/constants';

import './style.scss';

export function MarketingSpecialsPage() {
  const history = useHistory();
  const loginStore = useSelector((state: RootState) => state.loginStore);
  const { account } = loginStore;

  const handleCruiseOffers = () => {
    history.push(`/${account?.name}${Routes.Cruises}`);
  };

  const handleCondoOffers = () => {
    history.push(`/${account?.name}${Routes.Condo}`);
  };

  const handleMemberSpecials = () => {
    history.push(`/${account?.name}${Routes.Getaways}`);
  };

  return (
    <div className="marketing-specials-page">
      <Responsive>
        <div className="marketing-specials-container">
          {/* Hero Section */}
          <div className="hero-section">
            <div className="hero-content">
              <h1 className="hero-title">
                <FormattedMessage id="marketing.specials.title" defaultMessage="Weekly Specials" />
              </h1>
              <p className="hero-subtitle">
                <FormattedMessage 
                  id="marketing.specials.subtitle" 
                  defaultMessage="Discover amazing deals and exclusive offers updated weekly" 
                />
              </p>
            </div>
          </div>

          {/* Promotional Banners */}
          <div className="promo-banners-section">
            <h2 className="section-title">
              <FormattedMessage id="promo.banners.title" defaultMessage="Featured Promotions" />
            </h2>
            <div className="promo-banners-grid">
              <div className="promo-banner primary">
                <div className="banner-content">
                  <div className="banner-icon">
                    <FontAwesomeIcon icon={faPercent} />
                  </div>
                  <div className="banner-text">
                    <h3>Up to 50% Off</h3>
                    <p>Limited time offers on select destinations</p>
                  </div>
                </div>
                <div className="banner-overlay">
                  <button className="banner-button">
                    <FormattedMessage id="learn.more" defaultMessage="Learn More" />
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>

              <div className="promo-banner secondary">
                <div className="banner-content">
                  <div className="banner-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div className="banner-text">
                    <h3>Member Exclusive</h3>
                    <p>Special rates for our valued members</p>
                  </div>
                </div>
                <div className="banner-overlay">
                  <button className="banner-button">
                    <FormattedMessage id="view.offers" defaultMessage="View Offers" />
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>

              <div className="promo-banner tertiary">
                <div className="banner-content">
                  <div className="banner-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div className="banner-text">
                    <h3>Flash Sale</h3>
                    <p>24-hour deals that won't last long</p>
                  </div>
                </div>
                <div className="banner-overlay">
                  <button className="banner-button">
                    <FormattedMessage id="shop.now" defaultMessage="Shop Now" />
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Weekly Offers Sections */}
          <div className="weekly-offers-section">
            {/* Weekly Cruise Offers */}
            <div className="offer-section cruise-offers">
              <div className="section-header">
                <h2 className="section-title">
                  <FormattedMessage id="weekly.cruise.offers" defaultMessage="Weekly Cruise Offers" />
                </h2>
                <button className="view-all-button" onClick={handleCruiseOffers}>
                  <FormattedMessage id="view.all" defaultMessage="View All" />
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              
              <div className="offers-grid">
                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Save 30%</div>
                  </div>
                  <div className="offer-content">
                    <h3>Caribbean Paradise</h3>
                    <p>7-night cruise to the Caribbean</p>
                    <div className="offer-price">
                      <span className="original-price">$1,299</span>
                      <span className="sale-price">$909</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Limited Time</div>
                  </div>
                  <div className="offer-content">
                    <h3>Mediterranean Explorer</h3>
                    <p>10-night Mediterranean adventure</p>
                    <div className="offer-price">
                      <span className="original-price">$2,199</span>
                      <span className="sale-price">$1,539</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Member Special</div>
                  </div>
                  <div className="offer-content">
                    <h3>Alaska Wilderness</h3>
                    <p>8-night Alaska cruise with glacier viewing</p>
                    <div className="offer-price">
                      <span className="original-price">$1,899</span>
                      <span className="sale-price">$1,329</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Condo Offers */}
            <div className="offer-section condo-offers">
              <div className="section-header">
                <h2 className="section-title">
                  <FormattedMessage id="weekly.condo.offers" defaultMessage="Weekly Condo Offers" />
                </h2>
                <button className="view-all-button" onClick={handleCondoOffers}>
                  <FormattedMessage id="view.all" defaultMessage="View All" />
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              
              <div className="offers-grid">
                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Weekend Special</div>
                  </div>
                  <div className="offer-content">
                    <h3>Beachfront Condo</h3>
                    <p>2-bedroom oceanview condo in Miami</p>
                    <div className="offer-price">
                      <span className="original-price">$299/night</span>
                      <span className="sale-price">$199/night</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Extended Stay</div>
                  </div>
                  <div className="offer-content">
                    <h3>Mountain Retreat</h3>
                    <p>3-bedroom cabin in Aspen</p>
                    <div className="offer-price">
                      <span className="original-price">$450/night</span>
                      <span className="sale-price">$315/night</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Last Minute</div>
                  </div>
                  <div className="offer-content">
                    <h3>City Center Suite</h3>
                    <p>1-bedroom luxury suite in downtown</p>
                    <div className="offer-price">
                      <span className="original-price">$399/night</span>
                      <span className="sale-price">$279/night</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Member Specials */}
            <div className="offer-section member-specials">
              <div className="section-header">
                <h2 className="section-title">
                  <FormattedMessage id="weekly.member.specials" defaultMessage="Weekly Member Specials" />
                </h2>
                <button className="view-all-button" onClick={handleMemberSpecials}>
                  <FormattedMessage id="view.all" defaultMessage="View All" />
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
              
              <div className="offers-grid">
                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">VIP Access</div>
                  </div>
                  <div className="offer-content">
                    <h3>Exclusive Resort</h3>
                    <p>All-inclusive luxury resort package</p>
                    <div className="offer-price">
                      <span className="original-price">$1,599</span>
                      <span className="sale-price">$1,199</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Member Only</div>
                  </div>
                  <div className="offer-content">
                    <h3>Adventure Package</h3>
                    <p>Multi-day adventure tour with activities</p>
                    <div className="offer-price">
                      <span className="original-price">$899</span>
                      <span className="sale-price">$629</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>

                <div className="offer-card">
                  <div className="offer-image">
                    <div className="offer-badge">Premium Deal</div>
                  </div>
                  <div className="offer-content">
                    <h3>Cultural Experience</h3>
                    <p>Guided cultural tour with local experts</p>
                    <div className="offer-price">
                      <span className="original-price">$699</span>
                      <span className="sale-price">$489</span>
                    </div>
                    <button className="offer-button">Book Now</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter Signup */}
          <div className="newsletter-section">
            <div className="newsletter-content">
              <h2>Stay Updated</h2>
              <p>Get the latest weekly specials delivered to your inbox</p>
              <div className="newsletter-form">
                <input type="email" placeholder="Enter your email address" />
                <button className="subscribe-button">
                  <FormattedMessage id="subscribe" defaultMessage="Subscribe" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Responsive>
    </div>
  );
}
